import React from 'react';
import { Tab, Tabs, Box, Chip, Tooltip } from '@mui/material';

/**
 * DocumentSelector Component
 * Beautiful tab-based document switcher for multi-file reconciliation
 */
function DocumentSelector({
    documents,
    selectedDocument,
    setSelectedDocument,
    setCurrentPage,
}) {
    const handleDocumentChange = (event, newValue) => {
        setSelectedDocument(newValue);
        setCurrentPage(1);
    };

    // Single document view
    if (!documents || documents.length <= 1) {
        const doc = documents?.[0];
        return (
            <Box className="document-selector-single">
                <Chip 
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📄</span>
                            <span>{doc?.file_name || "Document 1"}</span>
                            {doc?.recordCount && (
                                <span style={{ 
                                    background: 'rgba(0,0,0,0.1)', 
                                    padding: '2px 8px', 
                                    borderRadius: '10px',
                                    fontSize: '0.75rem'
                                }}>
                                    {doc.recordCount} items
                                </span>
                            )}
                        </span>
                    }
                    color="primary"
                    variant="outlined"
                    size="medium"
                />
            </Box>
        );
    }

    return (
        <Box className="document-selector-container">
            <span className="document-selector-label">📁 Documents</span>
            
            <Tabs
                value={selectedDocument}
                onChange={handleDocumentChange}
                variant="scrollable"
                scrollButtons="auto"
                className="document-selector-tabs"
                sx={{
                    minHeight: '44px',
                    '& .MuiTabs-flexContainer': {
                        gap: '4px',
                    },
                    '& .MuiTab-root': {
                        minHeight: '40px',
                        padding: '8px 16px',
                        fontSize: '0.8rem',
                        textTransform: 'none',
                        borderRadius: '8px 8px 0 0',
                        border: '1px solid #e0e0e0',
                        borderBottom: 'none',
                        background: '#fff',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            background: '#e8f0fe',
                            borderColor: '#1a73e8',
                        },
                    },
                    '& .Mui-selected': {
                        background: '#e8f0fe !important',
                        color: '#1a73e8 !important',
                        fontWeight: '600 !important',
                        borderColor: '#1a73e8 !important',
                    },
                    '& .MuiTabs-indicator': {
                        background: 'linear-gradient(90deg, #1a73e8, #0d47a1)',
                        height: '3px',
                        borderRadius: '3px 3px 0 0',
                    },
                }}
            >
                {documents.map((doc, index) => (
                    <Tab
                        key={doc.document_number || index}
                        value={doc.document_number || index + 1}
                        label={
                            <Tooltip 
                                title={`${doc.file_name || `Document ${index + 1}`} (${doc.recordCount || 0} items)`}
                                arrow
                            >
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                }}>
                                    <span style={{ fontSize: '1rem' }}>
                                        {doc.hasErrors ? '📄' : '✅'}
                                    </span>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <span style={{ fontWeight: 500 }}>
                                            {doc.file_name 
                                                ? (doc.file_name.length > 12 
                                                    ? `${doc.file_name.substring(0, 12)}...` 
                                                    : doc.file_name)
                                                : `Doc ${doc.document_number || index + 1}`
                                            }
                                        </span>
                                        {doc.recordCount && (
                                            <span style={{ 
                                                fontSize: '0.65rem', 
                                                color: '#666',
                                                fontWeight: 400,
                                            }}>
                                                {doc.recordCount} items
                                            </span>
                                        )}
                                    </Box>
                                    {doc.hasErrors && (
                                        <span style={{ 
                                            color: '#ea4335', 
                                            fontSize: '0.9rem',
                                            marginLeft: '4px',
                                        }}>
                                            ⚠
                                        </span>
                                    )}
                                </Box>
                            </Tooltip>
                        }
                    />
                ))}
            </Tabs>
            
            <Box className="document-selector-info">
                <Chip 
                    label={
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontWeight: 600 }}>{documents.length}</span>
                            <span>files</span>
                        </span>
                    }
                    size="small"
                    sx={{
                        background: 'linear-gradient(135deg, #1a73e8, #0d47a1)',
                        color: 'white',
                        fontWeight: 500,
                        '& .MuiChip-label': {
                            padding: '0 10px',
                        },
                    }}
                />
            </Box>
        </Box>
    );
}

export default DocumentSelector;

